import { CreateUpdateContractClient } from "../../../client/types";
import { createUpdateContractClientToClient } from "../../../client/utils";
import { TravelCalcDraft } from "../../drafts/types";
import { CalcType, ClientExperience } from "../../enums";
import { TravelInsuranceType, TravelRiskGroup } from "./enums";
import {
  TravelCalc,
  TravelCalcCoveragesData,
  TravelCalcInsuredClient,
  TravelFormClients,
  TravelGen,
  TravelGenForm,
  TravelGenFormInsuredClient,
} from "./types";

export const RISK_GROUPS_BY_INSURANCE_TYPE: Partial<Record<TravelInsuranceType, TravelRiskGroup[]>> = {
  [TravelInsuranceType.SHORT_TERM]: [
    TravelRiskGroup.TOURIST,
    TravelRiskGroup.RISKY_SPORT,
    TravelRiskGroup.EXTREME_SPORT,
    TravelRiskGroup.PROFESSIONAL_SPORT,
    TravelRiskGroup.MANUAL_WORK,
    TravelRiskGroup.NON_MANUAL_WORK,
  ],
  [TravelInsuranceType.YEAR]: [
    TravelRiskGroup.TOURIST,
    TravelRiskGroup.SPORT,
    TravelRiskGroup.MANUAL_WORK,
    TravelRiskGroup.NON_MANUAL_WORK,
  ],
};

export const createTravelCalcObjectFromCalcData = (calcData: TravelCalc): TravelCalc => {
  let data = structuredClone(calcData);

  const cancellationData = calcData.cancellationData;
  if (cancellationData?.firstPaymentDate) {
    data = {
      ...data,
      cancellationData: {
        ...cancellationData,
      },
    };
  }

  const coveragesCancellationData = calcData.coveragesData?.cancellationData;
  if (coveragesCancellationData?.firstPaymentDate) {
    data = {
      ...data,
      coveragesData: {
        ...(data.coveragesData as TravelCalcCoveragesData),
        cancellationData: {
          ...coveragesCancellationData,
        },
      },
    };
  }

  return data;
};

export const createTravelFormClientsObject = (
  genData: TravelGen | TravelCalcDraft["draftData"],
): TravelFormClients => ({
  policyHolder: createUpdateContractClientToClient(genData.clientsData.clients[0] as CreateUpdateContractClient),
  representative: createUpdateContractClientToClient(genData.clientsData.clients[1] as CreateUpdateContractClient),
});

export const createTravelCalcObjectFromGenData = (genData: TravelGen | TravelCalcDraft["draftData"]) => {
  const genDataCopy = structuredClone(genData);
  const data = {
    type: CalcType.TRAVEL,
    generalData: genDataCopy.generalData,
    clientsData: {
      insuredClientsCount: genDataCopy.clientsData.insuredClientsCount,
      insuredClients: genDataCopy.clientsData.insuredClients
        ? genDataCopy.clientsData.insuredClients.map<TravelCalcInsuredClient>((client) => ({
            birthDate: client.birthDate,
            discountType: client.discountType,
          }))
        : null,
      insuredClientsAbroad: genDataCopy.clientsData.insuredClientsAbroad,
    },
    cancellationData: genDataCopy.cancellationData,
    coveragesData: genDataCopy.coveragesData,
    discountsData: genDataCopy.discountsData,
  } as TravelCalc & TravelGen;

  if (data.coveragesData) {
    delete data.coveragesData.vehicleAssistanceData;
    delete data.coveragesData.abandonedHouseholdData;
    delete data.coveragesData.petData;
  }

  return createTravelCalcObjectFromCalcData(data);
};

export const createTravelGenFormDataObject = (
  genData: TravelGen | TravelCalcDraft["draftData"],
  clients: TravelFormClients,
): TravelGenForm => {
  return {
    clientsData: {
      policyHolderIdentifier: clients.policyHolder?.identifier ?? "",
      policyHolderIsAlsoInsured: genData.clientsData.policyHolderIsAlsoInsured,
      policyHolderEmail: genData.clientsData.policyHolderEmail,
      policyHolderPhone: genData.clientsData.policyHolderPhone,
      policyHolderMarketingConsent: genData.clientsData.policyHolderMarketingConsent,
      representativeIdentifier: clients.representative?.identifier,
      representativeFunction: genData.clientsData.representativeFunction,
      insuredClientForFamilyInsurance: genData.clientsData.insuredClientForFamilyInsurance,
      insuredClients: genData.clientsData.insuredClients?.map<TravelGenFormInsuredClient>((client) => ({
        birthDate: client.birthDate,
        personalIdentificationNumber: client.personalIdentificationNumber,
        firstName: client.firstName ?? "",
        lastName: client.lastName ?? "",
        discountIdentifier: client.discountIdentifier,
      })),
    },
    coveragesData: genData.coveragesData
      ? {
          vehicleAssistanceData: genData.coveragesData.vehicleAssistanceData,
          abandonedHouseholdData: genData.coveragesData.abandonedHouseholdData,
          petData: genData.coveragesData.petData,
        }
      : undefined,
    financialMediationData: genData.financialMediationData ?? { clientExperience: "" as ClientExperience },
  };
};
